import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {createCompanyUser, getCompanyUserRoles} from "../api/api";
import {UserRole} from "../types";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from "@mui/material";
import AppModalBox from "./AppModalBox";

interface Props {
    companyId: number;
    onClose: () => void;
    onSuccess: () => void;
    onError: (error: string) => void;
}

const CompanyUserForm = ({companyId, onClose, onSuccess, onError}: Props) => {
    const {t} = useTranslation();

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [roles, setRoles] = useState<UserRole[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);

    const [loading, setLoading] = useState(false);
    const [rolesLoading, setRolesLoading] = useState(true);

    useEffect(() => {
        const loadRoles = async () => {
            try {
                const data = await getCompanyUserRoles();
                setRoles(data);
            } catch (e: any) {
                onError(e?.message || t("companyuserform.errors.loadRolesFailed"));
            } finally {
                setRolesLoading(false);
            }
        };

        loadRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!fullName.trim()) {
            onError(t("companyuserform.errors.fullNameRequired"));
            return;
        }

        if (!email.trim()) {
            onError(t("companyuserform.errors.emailRequired"));
            return;
        }

        if (!password.trim()) {
            onError(t("companyuserform.errors.passwordRequired"));
            return;
        }

        if (selectedRoles.length === 0) {
            onError(t("companyuserform.errors.atLeastOneRoleRequired"));
            return;
        }

        try {
            setLoading(true);

            await createCompanyUser(companyId, {
                fullName: fullName.trim(),
                email: email.trim(),
                password,
                roles: selectedRoles.map((role) => ({id: role.id})),
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            onError(error?.message || t("companyuserform.errors.addFailed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppModalBox sx={{margin: "auto", display: "flex", flexDirection: "column", gap: 2}}>
            <Typography variant="h6" sx={{mb: 2}}>
                {t("companyuserform.addCompanyUser")}
            </Typography>

            <form onSubmit={handleSubmit}>
                <TextField
                    label={t("companyuserform.fullName")}
                    fullWidth
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={loading}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("companyuserform.email")}
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("companyuserform.password")}
                    type="password"
                    fullWidth
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    sx={{mb: 2}}
                />

                <Autocomplete
                    multiple
                    options={roles}
                    loading={rolesLoading}
                    value={selectedRoles}
                    onChange={(_, value) => setSelectedRoles(value)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("companyuserform.roles")}
                            placeholder={
                                rolesLoading
                                    ? t("common.loading")
                                    : t("companyuserform.selectRoles")
                            }
                            disabled={loading || rolesLoading}
                            sx={{mb: 2}}
                        />
                    )}
                />

                <Box sx={{display: "flex", gap: 1}}>
                    <Button onClick={onClose} disabled={loading} fullWidth variant="outlined">
                        {t("common.cancel")}
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || rolesLoading || selectedRoles.length === 0}
                        fullWidth
                    >
                        {loading ? <CircularProgress size={22}/> : t("common.create")}
                    </Button>
                </Box>
            </form>
        </AppModalBox>
    );
};

export default CompanyUserForm;